const DEFAULT_ESTABLISHMENT_ID = 1;

export const roleLabels = {
  super_admin: "Super Admin",
  manager: "Manager",
  waiter: "Waiter",
  cuisini: "Cuisini",
};

const backendErrorMessages = {
  "Only managers can manage staff": "Seuls les managers peuvent gérer le personnel.",
  "Only managers can manage staff.": "Seuls les managers peuvent gérer le personnel.",
  "Only super admins can manage managers": "Seuls les super admins peuvent gérer les managers.",
  "Only super admin can perform this action.": "Seul le super admin peut effectuer cette action.",
  "User not found.": "Utilisateur introuvable.",
  "Unauthenticated.": "Session expirée. Veuillez vous reconnecter.",
  "This action is unauthorized.": "Vous n’êtes pas autorisé à effectuer cette action.",
  "The given data was invalid.": "Les informations saisies sont invalides.",
};

export const normalizeRole = (role) => {
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
};

const extractRoleValue = (role) => {
  if (!role) return "";

  if (typeof role === "string" || typeof role === "number") {
    return role;
  }

  if (typeof role === "object") {
    return (
      role.name ||
      role.slug ||
      role.code ||
      role.value ||
      role.key ||
      role.role ||
      ""
    );
  }

  return "";
};

export const getCurrentUserRole = (user) => {
  const possibleRoles = [
    user?.role?.name,
    user?.role?.slug,
    user?.role?.code,
    user?.role?.value,
    user?.role,

    user?.data?.role?.name,
    user?.data?.role?.slug,
    user?.data?.role?.code,
    user?.data?.role?.value,
    user?.data?.role,

    user?.user?.role?.name,
    user?.user?.role?.slug,
    user?.user?.role?.code,
    user?.user?.role?.value,
    user?.user?.role,

    user?.account?.role?.name,
    user?.account?.role,
    user?.profile?.role?.name,
    user?.profile?.role,

    user?.roles?.[0]?.name,
    user?.roles?.[0]?.slug,
    user?.roles?.[0]?.code,
    user?.roles?.[0]?.value,
    user?.roles?.[0],
  ];

  for (const possibleRole of possibleRoles) {
    const roleValue = extractRoleValue(possibleRole);

    if (roleValue) {
      return roleValue;
    }
  }

  return "";
};

export const isSuperAdminRole = (role) => {
  return ["superadmin"].includes(normalizeRole(role));
};

export const isManagerRole = (role) => {
  return normalizeRole(role) === "manager";
};

export const getUserEstablishmentId = (user) => {
  return (
    user?.establishment_id ||
    user?.data?.establishment_id ||
    user?.user?.establishment_id ||
    user?.establishment?.id ||
    user?.data?.establishment?.id ||
    user?.restaurant?.id ||
    user?.business?.id ||
    user?.managed_establishment_id ||
    user?.managedEstablishment?.id ||
    localStorage.getItem("selected_establishment_id") ||
    DEFAULT_ESTABLISHMENT_ID
  );
};

export const translateFallback = (t, key, fallback, params = {}) => {
  const value = t(key, params);
  const text = value === key ? fallback : value;

  return Object.entries(params).reduce((result, [paramKey, paramValue]) => {
    return result.replaceAll(`{{${paramKey}}}`, String(paramValue));
  }, text);
};

export const getApiFieldErrors = (error) => {
  return error?.response?.data?.errors || {};
};

export const translateApiErrorMessage = (message) => {
  if (!message) return "";

  return backendErrorMessages[message] || message;
};

export const getApiMessage = (error, fallback) => {
  const backendMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "";

  return translateApiErrorMessage(backendMessage) || fallback;
};

export const getFieldError = (fieldErrors, key) => {
  const value = fieldErrors?.[key];

  if (Array.isArray(value)) {
    return translateApiErrorMessage(value[0]);
  }

  return translateApiErrorMessage(value) || "";
};

export const getUserMode = (user) => {
  const role = getCurrentUserRole(user);

  if (isSuperAdminRole(role)) {
    return "managers";
  }

  if (isManagerRole(role)) {
    return "staff";
  }

  return "blocked";
};

export const canManageUsers = (user) => {
  const role = getCurrentUserRole(user);

  return isSuperAdminRole(role) || isManagerRole(role);
};